import React, { useState, useEffect, useMemo, useRef } from 'react';
import { SignUp, useAuth } from '@clerk/clerk-react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import './SignupChoice.css';

const API_BASE = process.env.REACT_APP_API_BASE_URL || '';

export default function SignupChoice() {
  const [step, setStep] = useState('choice'); // 'choice' | 'form' | 'signup' | 'auto-creating'
  const [orgName, setOrg] = useState('');
  const [checking, setChecking] = useState(false);
  const [available, setAvailable] = useState(null); // null | boolean
  const [touched, setTouched] = useState(false);
  const [creating, setCreating] = useState(false);
  const [autoCreateError, setAutoCreateError] = useState(null);
  const autoCreateAttempted = useRef(false);

  // eslint-disable-next-line no-unused-vars
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isSignedIn, isLoaded, getToken } = useAuth();

  const selectedPlan = useMemo(() => {
    try { return JSON.parse(localStorage.getItem('selectedPlan') || 'null') } catch { return null }
  }, []);

  // Check if returning from OAuth with pending org - only run once when auth loads
  const initialCheckDone = useRef(false);
  useEffect(() => {
    // Only run once after auth is loaded
    if (!isLoaded || initialCheckDone.current) return;
    initialCheckDone.current = true;

    const pendingName = localStorage.getItem('pending_org_name');
    if (pendingName && isSignedIn) {
      setStep('auto-creating');
    }
    // If no pending name, step stays at 'choice' (initial state)
  }, [isLoaded, isSignedIn]);

  // Require a selected plan before entering this flow
  useEffect(() => {
    const hasPlan = !!(selectedPlan && selectedPlan.tier && selectedPlan.priceId);
    if (!hasPlan) {
      const dest = encodeURIComponent('/signup-choice' + (window.location.search || ''));
      navigate(`/pricing?next=${dest}`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Create org helper with a hard redirect on success
  const createOrgNow = async (name, isAutoCreate = false) => {
    if (creating) return; // Prevent double-calls
    setCreating(true);
    if (isAutoCreate) setAutoCreateError(null);
    try {
      const token = await getToken();
      if (!token) {
        console.warn('No token available for org creation');
        if (isAutoCreate) {
          setAutoCreateError('Authentication not ready. Please try again.');
          setStep('form');
          setOrg(name);
          setTouched(true);
        } else {
          localStorage.setItem('pending_org_name', name);
          setStep('signup');
        }
        return;
      }
      const res = await fetch(`${API_BASE}/organizations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ name }),
        credentials: 'omit',
      });
      if (res.ok || res.status === 409) {
        localStorage.removeItem('pending_org_name');
        window.location.replace('/sign-in-gate');
        return;
      }
      console.error('Org create failed:', res.status);
      if (isAutoCreate) {
        localStorage.removeItem('pending_org_name');
        setAutoCreateError('Could not create organization. Please try again.');
        setStep('form');
        setOrg(name);
        setTouched(true);
      }
    } catch (err) {
      console.error('Org create error:', err);
      if (isAutoCreate) {
        localStorage.removeItem('pending_org_name');
        setAutoCreateError('Could not create organization. Please try again.');
        setStep('form');
        setOrg(name);
        setTouched(true);
      }
    } finally {
      setCreating(false);
    }
  };

  // Auto-create org when returning from OAuth with pending org name
  useEffect(() => {
    if (step !== 'auto-creating') return;
    if (autoCreateAttempted.current) return;
    
    const pendingName = localStorage.getItem('pending_org_name');
    if (pendingName && isSignedIn && isLoaded) {
      autoCreateAttempted.current = true;
      createOrgNow(pendingName, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, isSignedIn, isLoaded]);

  // Debounced availability check
  useEffect(() => {
    if (step !== 'form') return;
    const trimmed = orgName.trim();
    if (!touched || trimmed.length < 3) { setAvailable(null); setChecking(false); return; }
    let active = true;
    const controller = new AbortController();
    setChecking(true);
    const handle = setTimeout(async () => {
      try {
        const token = await getToken();
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const res = await fetch(
          `${API_BASE}/organizations/availability?name=${encodeURIComponent(trimmed)}`,
          {
            signal: controller.signal,
            credentials: 'omit',
            headers
          }
        );
        if (!res.ok) throw new Error('availability check failed');
        const data = await res.json();
        if (active) setAvailable(!!data.available);
      } catch { if (active) setAvailable(null); }
      finally { if (active) setChecking(false); }
    }, 300);
    return () => { active = false; controller.abort(); clearTimeout(handle); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgName, step, touched]);

  const PlanBanner = () => {
    if (!selectedPlan?.tier) return null;
    const pretty =
      selectedPlan.tier === 'free' ? 'Free' :
      selectedPlan.tier === 'pro' ? 'Pro ($199/mo)' :
      selectedPlan.tier === 'enterprise' ? 'Enterprise ($499/mo)' :
      selectedPlan.tier;
    return (
      <div className="plan-banner">
        <div><b>Selected plan:</b> {pretty}</div>
        <Link to="/pricing" className="plan-banner-link">Change plan</Link>
      </div>
    );
  };

  // Show loading state when auto-creating org after OAuth return
  if (step === 'auto-creating') {
    return (
      <div className="onboard-wrapper">
        <PlanBanner />
        <h2>Setting Up Your Organization…</h2>
        <p className="small" style={{ marginTop: -8, marginBottom: 12 }}>
          Please wait while we create your organization and set up your account.
        </p>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, color: '#6b7280', marginTop: 24 }}>
          <svg width="20" height="20" viewBox="0 0 50 50" aria-hidden="true">
            <circle cx="25" cy="25" r="20" stroke="currentColor" strokeWidth="6" fill="none" strokeLinecap="round">
              <animateTransform attributeName="transform" attributeType="XML" type="rotate" from="0 25 25" to="360 25 25" dur="0.75s" repeatCount="indefinite" />
            </circle>
          </svg>
          Creating organization…
        </div>
      </div>
    );
  }

  if (step === 'signup') {
    return (
      <div className="onboard-wrapper">
        <PlanBanner />
        <h2>Create&nbsp;Your&nbsp;Admin&nbsp;Account</h2>
        <p className="small" style={{ marginTop: -8, marginBottom: 12 }}>
          Your account will be tied to the new organization you just named.
        </p>
        <SignUp
          routing="virtual"
          afterSignUp={() => window.location.replace('/sign-in-gate')}
        />
        <p className="small">Already have an account? <a href="/sign-in">Log&nbsp;in</a></p>
      </div>
    );
  }

  if (step === 'form') {
    const trimmed = orgName.trim();
    const canContinue = trimmed.length >= 3 && available === true && !checking && !creating;

    const handleContinue = async () => {
      if (!canContinue) return;
      if (isSignedIn) { await createOrgNow(trimmed); return; }
      localStorage.setItem('pending_org_name', trimmed);
      setOrg(trimmed);
      setStep('signup');
    };

    const handleInputChange = (e) => {
      e.stopPropagation();
      setOrg(e.target.value);
      if (!touched) setTouched(true);
    };

    const handleInputClick = (e) => {
      e.stopPropagation();
    };

    const handleInputFocus = (e) => {
      e.stopPropagation();
    };

    return (
      <section className="onboard-choice" onClick={(e) => e.stopPropagation()}>
        <PlanBanner />
        <h1>Create&nbsp;a&nbsp;New&nbsp;Organization</h1>
        {autoCreateError && (
          <div className="feedback-error" style={{ marginBottom: 16 }}>
            {autoCreateError}
          </div>
        )}
        <p className="onboard-sub">
          You'll be the first admin linked to this organization. The name must be
          unique, but a project codename is fine.
        </p>
        <div className="org-name-form" onClick={(e) => e.stopPropagation()}>
          <label htmlFor="orgName">Organization&nbsp;Name</label>
          <input
            id="orgName"
            type="text"
            value={orgName}
            onChange={handleInputChange}
            onClick={handleInputClick}
            onFocus={handleInputFocus}
            onMouseDown={(e) => e.stopPropagation()}
            placeholder="Acme Corp or mynamemyprojectisthebomb"
            autoFocus
            disabled={creating}
            style={{ position: 'relative', zIndex: 10 }}
          />
          {(() => {
            if (!touched || trimmed.length === 0) return null;
            if (trimmed.length < 3) return <div className="feedback-error">Organization name must be at least 3 letters long</div>;
            if (checking) return (
              <div className="feedback-info" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <svg width="12" height="12" viewBox="0 0 50 50" aria-hidden="true">
                  <circle cx="25" cy="25" r="20" stroke="currentColor" strokeWidth="6" fill="none" strokeLinecap="round">
                    <animateTransform attributeName="transform" attributeType="XML" type="rotate" from="0 25 25" to="360 25 25" dur="0.75s" repeatCount="indefinite" />
                  </circle>
                </svg>
                Checking…
              </div>
            );
            if (available === false) return <div className="feedback-error">Organization name already taken</div>;
            if (available === true) return <div className="feedback-success">Organization name is available</div>;
            return null;
          })()}
          <button className="btn btn-primary btn-block" disabled={!canContinue} onClick={handleContinue} style={{ marginTop: '10px' }}>
            {creating ? 'Creating…' : 'Continue →'}
          </button>
        </div>
        <p className="small">Already have an account? <a href="/sign-in">Log&nbsp;in</a></p>
      </section>
    );
  }

  return (
    <section className="onboard-choice">
      <PlanBanner />
      <h1>Welcome&nbsp;to&nbsp;IQLedger</h1>
      <button className="choice-card" onClick={() => setStep('form')}>
        <h2>Create&nbsp;a&nbsp;New&nbsp;Organization</h2>
        <p>You’ll enter the org name next.</p>
      </button>
      <h3 className="existing-note">
        Does your organization already have an account?&nbsp;
        Ask your admin for an invite link.
      </h3>
      <p className="small">Already have an account? <a href="/sign-in">Log&nbsp;in</a></p>
    </section>
  );
}